import React, { useMemo } from 'react';
import { Dropdown, SearchBox } from 'tea-component';
import useFilterData from './hooks/useFilterData';
import useFilterSearch from './hooks/useFilterSearch';
import useFilterLocale from './hooks/useLocale';
import FilterSelectButton from './components/FilterSelectButton';
import OptionsGroup from './components/OptionsGroup';
import { LOCALE_CN } from './config';
import { ISelectOptionItemType, IGroupSelectPropsType } from './type';
import CommonBubble from '../CommonBubble';
import classNames from 'classnames';

import './index.scss';

const SingleSelect = (props: IGroupSelectPropsType) => {
  const {
    options,
    groups, // 分组
    className = '',
    boxClassName = '',
    overlayClassName = '',
    boxStyle = {},
    placeholder,
    // virtual = true, 后续扩展
    searchable = true,
    searchValue: propsSearchValue,
    defaultValue,
    value,
    onChange, // 选项发生改变的回调
    listTitle, // 选项框头部标题
    listWidth = 419, // 选项框宽度
    listHeight, // 选项框高度
    name, // button展示的固定字段
    button,
    autoClearSearchValue = true, // 是否自动清除搜索值
    searchPlaceholder,
    onSearch,
    defaultSearchValue,
    onSearchValueChange,
    header, // 下拉框头部内容
    footer, // 下拉框尾部内容
    onClose, // 下拉框关闭的回调
    onOpen, // 下拉框打开的回调
    style,
    locale = LOCALE_CN, // 中英文控制
    language, //
    // 筛选器浮窗解释
    tooltip,
    ...restProps
  } = props;

  const { formatMessage } = useFilterLocale(locale, language);

  const {
    optionsValue,
    isOpen,
    handleClearAll,
    handleOpen,
    handleClose,
    handleSelectChange,
    isInit,
  } = useFilterData(props);

  const { searchValue, renderOptions, handleSearchValueChange } =
    useFilterSearch({
      options,
      searchValue: propsSearchValue,
      defaultSearchValue,
      onSearchValueChange,
      autoClearSearchValue,
      isInit,
    });

  // 筛选器文本框处理
  const filterButton = useMemo(() => {
    // 筛选项options处理
    const selectedOption =
      [...(options || [])]?.find(
        (optionItem: ISelectOptionItemType) =>
          optionsValue === optionItem?.value,
      ) || null;
    // 传入button可使用ReactNode和函数方式渲染
    if (button)
      return typeof button === 'function' ? button(selectedOption) : button;
    // 默认的筛选器文本框渲染
    return (
      <FilterSelectButton
        selectedOption={selectedOption}
        placeholder={placeholder}
        name={name}
      />
    );
  }, [optionsValue, placeholder, options, button, name]);

  const { isMatch } = useMemo(() => {
    // 是否搜索为空
    const isMatch = renderOptions?.length > 0;
    // 是否为清除按钮
    const isClear = !!optionsValue;
    return { isMatch, isClear };
  }, [optionsValue, renderOptions]);

  return (
    <CommonBubble
      dark
      escapeWithReference
      flipped={false}
      content={tooltip && !isOpen ? tooltip : null}
    >
      <Dropdown
        className={classNames('db-single-select', className)}
        overlayClassName={classNames('db-single-select-overlay', overlayClassName)}
        boxClassName={classNames('db-single-select-box', boxClassName)}
        boxStyle={
          listWidth ? { ...boxStyle, width: `${listWidth}px` } : { ...boxStyle }
        }
        style={style}
        appearance="button"
        clickClose={false}
        closeOnScroll={true}
        button={filterButton}
        onClear={() =>
          handleClearAll()
        }
        open={isOpen}
        onOpen={() => {
          // 清空操作放在close中会导致结果闪动的问题，所以放在open方法中处理
          !propsSearchValue && handleSearchValueChange('');
          handleOpen();
        }}
        onClose={handleClose}
        {...restProps}
      >
        <div className={`db-single-select-list-header`}>{header}</div>
        {searchable && (
          <SearchBox
            placeholder={
              searchPlaceholder
                ? searchPlaceholder
                : formatMessage({ id: 'placeholder' })
            }
            className="db-single-select-search"
            onChange={handleSearchValueChange}
            onSearch={onSearch}
            value={searchValue}
          />
        )}
        <div className="db-single-select-content">
          {options?.length > 0 ? (
            <OptionsGroup
              value={optionsValue}
              listHeight={listHeight}
              isMatch={isMatch}
              options={renderOptions}
              formatMessage={formatMessage}
              onChange={handleSelectChange}
              groups={groups}
              listTitle={listTitle}
            />
          ) : (
            <div className="db-single-select-no-data">
              {formatMessage({ id: 'no_data' })}
            </div>
          )}
        </div>
        <div className={`db-single-select-list-footer`}>{footer}</div>
      </Dropdown>
    </CommonBubble>
  );
};

export default SingleSelect;
